import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';

// The points are the number in "(n)" at the start of the answer: 0, 5, 10 or
// 15. The first answer is worth 10 for feeding, 5 for bathing and grooming and
// 15 for transfers and mobility, so never use the answer's position.
const BARTHEL_POINTS_REGEX = /^\s*\(\s*(\d+)\s*\)/;
const BARTHEL_ALLOWED_POINTS = [0, 5, 10, 15];

// Titles separate the instruction with "||" or "‖".
const BARTHEL_TITLE_SEPARATOR = /\|\||‖/;

// The 10 scored items, in the order of the form.
const BARTHEL_ITEMS = [
  'question854', // Feeding
  'question855', // Bathing
  'question856', // Grooming
  'question857', // Dressing
  'question858', // Bowels
  'question859', // Bladder
  'question860', // Toilet use
  'question861', // Transfers
  'question862', // Mobility
  'question863', // Stairs
];
// The total is only shown, never saved: the backend scores the items itself.
export const BARTHEL_TOTAL_CONTROL_NAME = 'question864';
const BARTHEL_MAX_SCORE = 100;

@Component({
  selector: 'app-barthel-questions',
  templateUrl: './barthel-questions.component.html',
  styleUrl: './barthel-questions.component.css',
  standalone: false,
})
export class BarthelQuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  // The page title holds the test's heading and instruction.
  @Input() pageTitle = '';
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  readonly maxScore = BARTHEL_MAX_SCORE;
  // Live total of the items answered so far, 0 while none is.
  totalScore = 0;

  private sub?: Subscription;

  ngOnInit(): void {
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateTotal();
    });
    this.recalculateTotal();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null);
    }
  }

  getQuestionTitle(title?: string): string {
    return title?.split(BARTHEL_TITLE_SEPARATOR)[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split(BARTHEL_TITLE_SEPARATOR)[1]?.trim() ?? '';
  }

  isTotalScoreElement(element: IQuestionnaireQuestion): boolean {
    return element.name === BARTHEL_TOTAL_CONTROL_NAME;
  }

  private recalculateTotal(): void {
    this.totalScore = BARTHEL_ITEMS.map((controlName) =>
      this.getSelectedPoints(controlName),
    )
      .filter((point): point is number => point !== null)
      .reduce((sum, point) => sum + point, 0);
  }

  // Points come from the selected choice text, never from its position.
  private getSelectedPoints(controlName: string): number | null {
    const selectedValue = this.formGroup.get(controlName)?.value;
    if (
      selectedValue === null ||
      selectedValue === undefined ||
      selectedValue === ''
    ) {
      return null;
    }

    const choice = (
      this.elements?.find((el) => el.name === controlName)?.choices ?? []
    ).find((c: IQuestionnaireChoice) => c.value === selectedValue);
    const match = (choice?.text ?? '').match(BARTHEL_POINTS_REGEX);
    const points = match ? Number(match[1]) : null;

    return points !== null && BARTHEL_ALLOWED_POINTS.includes(points)
      ? points
      : null;
  }
}
